/**
 * ChatCartPanel — Collapsible inline cart at the bottom of the chat widget.
 * Shows items with qty controls and per-item notes. No delivery instructions
 * (handled at checkout).
 */

import {
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Delete as DeleteIcon,
  EditNote as NotesIcon,
  ExpandLess,
  ExpandMore,
  Remove as RemoveIcon,
  ShoppingBag as BagIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useCartStore, useCartSubtotal } from '@/store/useCartStore';
import type { CartItem } from '@/types';

// ─── Single item row ─────────────────────────────────────────────────────────

interface CartItemRowProps {
  item: CartItem;
  language: 'en' | 'ar';
  onCartChanged?: () => void;
}

function CartItemRow({ item, language, onCartChanged }: CartItemRowProps) {
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const updateItem = useCartStore((s) => s.updateItem);
  const removeItem = useCartStore((s) => s.removeItem);
  const [showNotes, setShowNotes] = useState(!!item.notes);
  const [notesValue, setNotesValue] = useState(item.notes || '');
  const isAr = language === 'ar';

  const itemTotal =
    (item.unit_price + item.modifiers.reduce((s, m) => s + m.price_adjustment, 0)) * item.quantity;

  const handleNotesBlur = () => {
    updateItem(item.id, { notes: notesValue.trim() || undefined });
  };

  return (
    <Box sx={{ py: 0.75 }}>
      {/* Row: qty | name+modifiers | price | notes-btn | delete-btn */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {/* Qty stepper */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => { updateQuantity(item.id, item.quantity - 1); onCartChanged?.(); }}
            sx={{ width: 22, height: 22, border: '1px solid', borderColor: 'divider' }}
          >
            <RemoveIcon sx={{ fontSize: 11 }} />
          </IconButton>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, minWidth: 16, textAlign: 'center', fontSize: '0.76rem' }}
          >
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => { updateQuantity(item.id, item.quantity + 1); onCartChanged?.(); }}
            sx={{ width: 22, height: 22, border: '1px solid', borderColor: 'divider' }}
          >
            <AddIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Box>

        {/* Name + modifiers — takes all available space */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.74rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              direction: isAr ? 'rtl' : 'ltr',
            }}
          >
            {item.name}
          </Typography>
          {item.modifiers.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.64rem',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.modifiers.map((m) => m.name).join(', ')}
            </Typography>
          )}
          {item.notes && !showNotes && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.63rem',
                fontStyle: 'italic',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              📝 {item.notes}
            </Typography>
          )}
        </Box>

        {/* Price */}
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: '0.74rem', flexShrink: 0, ml: 0.25 }}
        >
          £{itemTotal.toFixed(2)}
        </Typography>

        {/* Notes toggle */}
        <Tooltip title={isAr ? 'ملاحظات' : 'Notes'}>
          <IconButton
            size="small"
            onClick={() => setShowNotes((v) => !v)}
            sx={{
              color: showNotes || item.notes ? 'primary.main' : 'text.disabled',
              width: 24,
              height: 24,
              flexShrink: 0,
            }}
          >
            <NotesIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>

        {/* Remove */}
        <Tooltip title={isAr ? 'حذف' : 'Remove'}>
          <IconButton
            size="small"
            onClick={() => { removeItem(item.id); onCartChanged?.(); }}
            sx={{ color: 'error.light', width: 24, height: 24, flexShrink: 0 }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Notes field — full width below the row */}
      <Collapse in={showNotes} unmountOnExit>
        <TextField
          size="small"
          multiline
          maxRows={2}
          placeholder={isAr ? 'ملاحظات خاصة (بدون بصل، إلخ)' : 'e.g. no onions, extra sauce…'}
          value={notesValue}
          onChange={(e) => setNotesValue(e.target.value)}
          onBlur={handleNotesBlur}
          fullWidth
          inputProps={{ style: { fontSize: '0.72rem' } }}
          sx={{
            mt: 0.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              fontSize: '0.72rem',
            },
          }}
        />
      </Collapse>
    </Box>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

interface ChatCartPanelProps {
  language: 'en' | 'ar';
  onCheckout?: () => void;
  onCartChanged?: () => void;
}

export default function ChatCartPanel({ language, onCheckout, onCartChanged }: ChatCartPanelProps) {
  const theme = useTheme();
  const items = useCartStore((s) => s.items);
  const subtotal = useCartSubtotal();
  const isAr = language === 'ar';
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0) return null;

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <Box
      sx={{
        borderTop: `2px solid ${theme.palette.primary.main}`,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
        flexShrink: 0,
        maxHeight: expanded ? 260 : 'auto',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Clickable header */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
          flexShrink: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <BagIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, fontSize: '0.78rem' }}>
          {isAr
            ? `طلبك (${totalQty} عناصر)`
            : `Your order (${totalQty} item${totalQty !== 1 ? 's' : ''})`}
        </Typography>
        <Chip
          label={`£${subtotal.toFixed(2)}`}
          size="small"
          color="primary"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
        />
        {expanded ? (
          <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
        )}
      </Box>

      {/* Scrollable item list */}
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 1.5,
            pb: 0.5,
            overflowY: 'auto',
            maxHeight: 148,
          }}
        >
          <Divider sx={{ mb: 0.5 }} />
          {items.map((item, idx) => (
            <Box key={item.id}>
              <CartItemRow item={item} language={language} onCartChanged={onCartChanged} />
              {idx < items.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>

        {/* Checkout button */}
        {onCheckout && (
          <Box sx={{ px: 1.5, pb: 1.25, pt: 0.5, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={onCheckout}
              sx={{
                borderRadius: 2,
                fontSize: '0.8rem',
                fontWeight: 700,
                py: 0.75,
                width: '100%',
                maxWidth: 360,
              }}
            >
              {isAr ? `الدفع • £${subtotal.toFixed(2)}` : `Checkout • £${subtotal.toFixed(2)}`}
            </Button>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
